/** @jsx jsx */
import { jsx } from '@emotion/react'
import { css } from '@emotion/react/macro'
import { useEffect, useState } from 'react'
import { OnChangeEventContext } from 'react-crane'
import EditIcon from '@mui/icons-material/Edit'

import { BoardGroupFragment, CatalogSearchResultFragment } from '../../../../generated/graphql'
import { Select, Switch, TextField } from '../../../shared/input'
import RichTextField from '../../../shared/input/RichTextField'
import CourseAutoComplete from '../../../courses/CourseAutoComplete'
import { SecondaryButton } from '../../../shared/buttons'
import { BP_SMALL_DOWN, BP_SMALL_UP } from '../../../../lib/styles/breakpoints'
import { offsetGray2, primaryTextColor, secondaryGrayColor } from '../../../../lib/styles/colors'
import { fieldsetCss, srOnlyCss } from '../../../../lib/styles/cssStyles'
import { subtitle1Css, tertiaryBodyCss } from '../../../../lib/styles/typography'
import useAppSettings from '../../../../lib/hooks/useAppSettings'

const creditsWrapperCss = css`
  ${fieldsetCss}
  padding-top: 20px;
`
const creditsFieldsCss = css`
  display: inline-flex;
  width: 170px;
`
const titleFieldCss = css`
  margin-bottom: 34px;
`
const innerFormCss = css`
  display: grid;
  @media (${BP_SMALL_UP}) {
    grid-template-columns: 2fr 1fr;
    column-gap: 3rem;
  }
`
const col1Css = css`
  grid-column: 1;
  max-width: 360px;
`
const creditsDividerCss = css`
  border-top: 1px solid ${primaryTextColor};
  display: inline;
  font-size: 12px;
  padding-left: 10px;
`
const descWrapperCss = css`
  grid-column: 1 / span 2;
  padding-top: 20px;
`
const switchLabelCss = css`
  .switchLabel {
    ${subtitle1Css};
  }
`
const switchesCss = css`
  grid-row: 1;
  grid-column: 2;
  @media (${BP_SMALL_DOWN}) {
    grid-column: 1;
    grid-row: auto;
    padding-left: 10px;
  }
  ${switchLabelCss}
`
const placeholderSwitchCss = css`
  ${switchLabelCss}
  display: flex;
  justify-content: space-between;
`
const dividerCss = css`
  border-bottom: 1px solid ${secondaryGrayColor};
  margin: 16px 8px 12px 8px;
`
const courseOptionsCss = css`
  margin-left: 8px;
  margin-right: 8px;
`
const courseOptionsEditCss = css`
  background: ${offsetGray2};
  padding: 12px 24px;
`
const instructionsCss = css`
  ${tertiaryBodyCss}
  padding-bottom: 8px;
`
const closeButtonWrapperCss = css`
  display: grid;
  margin-top: 4px;
`
const closeButtonCss = css`
  justify-self: end;
  width: 80px;
`

type NonCourseFieldsProps = {
  /** Campus used to filter courses */
  campus?: string
  /** Label for course */
  courseLabel: string
  /** Label for credits */
  creditsLabel: string
  /** Minimum number of credits for the placeholder */
  creditsMin?: number | undefined
  /** Maximum number of credits for the placeholder */
  creditsMax?: number | undefined
  /** Description of Plan Item */
  description: string | undefined
  /** Indicates if the placeholders are being edited after the initial save.  */
  editPlaceholdersMode: boolean
  /** Current GroupId of Plan Item */
  groupId: string | null | undefined
  /** Whether the plan item is a placeholder that the student must choose a course for as it is currently saved in the db */
  isCoursePlaceholder: boolean
  /** The current selection of isCoursePlaceholder persisted in state */
  isCoursePlaceholderState: boolean
  /** Whether the user is a student. */
  isStudent: boolean
  /** Whether the item has been locked by an admin */
  locked: boolean
  /** Event callback input/select in form has changed  */
  onChange: (
    name: string,
    value:
      | string
      | BoardGroupFragment
      | boolean
      | number
      | CatalogSearchResultFragment
      | CatalogSearchResultFragment[]
      | undefined
  ) => void
  /** All Plan Groups for Current Plan */
  onEditPlaceholdersModeChange: (value: boolean) => void
  /** Callback when the isCoursePlaceholder switch changes */
  onIsCoursePlaceholderChange: () => void
  planGroups: Array<BoardGroupFragment>
  /** Whether all of the inputs and buttons should be disabled */
  readOnly: boolean
  /** Selected course for placeholder */
  selectedCourse: CatalogSearchResultFragment | undefined
  /** Selected course for placeholder */
  selectedCoursePlaceholders: CatalogSearchResultFragment[] | undefined
  /** Whether the item has been required by an admin */
  required: boolean
  /** Label for term */
  termLabel: string
  /** title of Plan Item */
  title: string
}

const defaultProps: Pick<NonCourseFieldsProps, 'campus' | 'creditsMin' | 'creditsMax'> = {
  campus: undefined,
  creditsMin: undefined,
  creditsMax: undefined
}

const NonCourseFields = ({
  campus,
  courseLabel,
  creditsLabel,
  creditsMax,
  creditsMin,
  description,
  editPlaceholdersMode,
  readOnly,
  groupId,
  isCoursePlaceholder,
  isCoursePlaceholderState,
  isStudent,
  locked,
  onChange,
  onEditPlaceholdersModeChange,
  onIsCoursePlaceholderChange,
  planGroups,
  selectedCourse,
  selectedCoursePlaceholders,
  required,
  termLabel,
  title
}: NonCourseFieldsProps) => {
  const [creditsMinError, setCreditsMinError] = useState(false)
  const [creditsMaxError, setCreditsMaxError] = useState(false)
  const [creditsMinErrorText, setCreditsMinErrorText] = useState('')
  const creditsLabelLower = creditsLabel.toLowerCase()
  const { text } = useAppSettings()
  const { coursePlural } = text

  // Adding this validation in useEffect rather than onBlur because there is existing data that has a max with no min
  // and the error should be shown to the user to explain why they can't save the item
  useEffect(() => {
    if (!creditsMinError || !creditsMax) {
      if (!creditsMin && creditsMin !== 0 && creditsMax) {
        setCreditsMinErrorText('This field is required when entering a max value')
        setCreditsMinError(true)
      } else {
        setCreditsMinError(false)
      }
    }
  }, [creditsMinError, creditsMin, creditsMax])

  const handleCreditsMinBlur = () => {
    if (creditsMin && creditsMax && creditsMin > creditsMax) {
      setCreditsMinErrorText(
        `Minimum ${creditsLabelLower} cannot be greater than maximum ${creditsLabelLower}`
      )
      setCreditsMinError(true)
      onChange('nonCourseCreditsMin', NaN)
    } else {
      setCreditsMinError(false)
    }
  }
  const handleCreditsMaxBlur = () => {
    if (creditsMin && creditsMax && creditsMax < creditsMin) {
      setCreditsMaxError(true)
      onChange('nonCourseCreditsMax', NaN)
    } else {
      setCreditsMaxError(false)
    }
  }

  const handleCreditsChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.name, parseFloat(e.target.value))
  }
  const handleDescriptionChange = (value: string) => {
    onChange('nonCourseDescription', value)
  }
  const handleGroupChange = (e: OnChangeEventContext) => {
    const group = e.value as BoardGroupFragment
    onChange(e.name, group)
  }
  const handleTitleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    onChange('nonCourseTitle', e.target.value)
  }
  const handleLockChange = () => {
    onChange('locked', !locked)
  }
  const handlePlaceholderChange = (
    courses: CatalogSearchResultFragment | CatalogSearchResultFragment[]
  ) => {
    onChange('selectedCoursePlaceholders', courses)
  }

  const onEditPlaceholdersClick = () => {
    onEditPlaceholdersModeChange(true)
  }
  const handleRequiredChange = () => {
    onChange('required', !required)
  }

  const onStopEditPlaceholdersClick = () => {
    onEditPlaceholdersModeChange(false)
    // If there was a selected course in the placeholders list that is no longer there, clear the selection
    if (selectedCoursePlaceholders?.length && selectedCourse) {
      const course = selectedCoursePlaceholders.find(
        (placeholder) => placeholder.externalCourseId === selectedCourse.externalCourseId
      )
      if (!course) {
        onChange('selectedCourse', '')
      }
    }
  }
  const isSavedPlaceholder = isCoursePlaceholder && isCoursePlaceholderState
  return (
    <div>
      <div css={!isStudent && innerFormCss}>
        <div css={col1Css}>
          <TextField
            id="title"
            css={title && titleFieldCss}
            disabled={readOnly}
            label="Title"
            name="title"
            onChange={handleTitleChange}
            error={!title}
            helperText={title ? '' : 'This field is required'}
            value={title}
          />
          <Select
            id={termLabel.toLowerCase()}
            disabled={(locked && isStudent) || readOnly}
            label={termLabel}
            labelKey="title"
            name="group"
            onChange={handleGroupChange}
            options={planGroups}
            value={groupId}
            valueKey="id"
          />
        </div>
        <fieldset css={creditsWrapperCss}>
          <legend>
            <span css={srOnlyCss}>{`${creditsLabel} range`}</span>
          </legend>
          <div css={creditsFieldsCss}>
            <TextField
              disabled={readOnly}
              error={creditsMinError}
              helperText={creditsMinError ? creditsMinErrorText : undefined}
              id="nonCourseCreditsMin"
              inputType="number"
              label={`Minimum ${creditsLabel}`}
              name="nonCourseCreditsMin"
              onBlur={handleCreditsMinBlur}
              onChange={handleCreditsChange}
              value={creditsMin !== undefined && creditsMin >= 0 ? creditsMin : ''}
            />
          </div>
          <div css={creditsDividerCss}> </div>
          <div css={creditsFieldsCss}>
            <TextField
              disabled={readOnly}
              error={creditsMaxError}
              helperText={
                creditsMaxError
                  ? `Maximum ${creditsLabelLower} cannot be less than minimum ${creditsLabelLower}`
                  : undefined
              }
              id="nonCourseCreditsMax"
              inputType="number"
              label={`Maximum ${creditsLabel}`}
              name="nonCourseCreditsMax"
              onBlur={handleCreditsMaxBlur}
              onChange={handleCreditsChange}
              value={creditsMax !== undefined && creditsMax >= 0 ? creditsMax : ''}
            />
          </div>
        </fieldset>
        <div css={descWrapperCss}>
          <RichTextField
            disabled={readOnly}
            id="description"
            label="Description"
            name="description"
            onChange={handleDescriptionChange}
            rows={5}
            value={description}
          />
        </div>
        {!isStudent && (
          <div css={switchesCss}>
            <Switch
              ariaLabel={`${required ? 'Unrequire' : 'Require'} this ${courseLabel}`}
              checked={required}
              disabled={readOnly}
              id="required"
              infoText={`Requiring an item means a student cannot delete it but it can be moved to another ${termLabel.toLowerCase()}.`}
              label="Required Item"
              labelCssClassName="switchLabel"
              onChange={handleRequiredChange}
            />
            <Switch
              ariaLabel={`${locked ? 'Unlock' : 'Lock'} this ${courseLabel}`}
              checked={locked}
              disabled={readOnly}
              id="locked"
              infoText={`Locking an item means a student cannot delete it or move it to another ${termLabel.toLowerCase()}.`}
              label="Locked Item"
              labelCssClassName="switchLabel"
              onChange={handleLockChange}
            />
          </div>
        )}
      </div>
      {!isStudent && (
        <div>
          <div css={dividerCss} />
          <div css={placeholderSwitchCss}>
            <Switch
              ariaLabel={
                isCoursePlaceholderState
                  ? `Deselect as ${courseLabel} Placeholder`
                  : `Select as ${courseLabel} Placeholder`
              }
              disabled={readOnly}
              id="isCoursePlaceholder"
              label={`${courseLabel} Placeholder`}
              labelCssClassName="switchLabel"
              onChange={onIsCoursePlaceholderChange}
              checked={isCoursePlaceholderState}
            />
            {isSavedPlaceholder && (
              <SecondaryButton
                disabled={editPlaceholdersMode || readOnly}
                onClick={onEditPlaceholdersClick}
                startIcon={<EditIcon />}
              >
                Edit Placeholder Details
              </SecondaryButton>
            )}
          </div>
          {((!isCoursePlaceholder && isCoursePlaceholderState) ||
            (isCoursePlaceholderState && editPlaceholdersMode)) && (
            <div css={[courseOptionsCss, editPlaceholdersMode && courseOptionsEditCss]}>
              <div css={instructionsCss}>
                {`Enter the ${coursePlural.toLowerCase()} that can be completed to fulfill this placeholder requirement.`}
              </div>
              <CourseAutoComplete
                campus={campus}
                disabled={readOnly || !isCoursePlaceholderState}
                isMultiSelect
                groupId={groupId || ''}
                label={coursePlural}
                onChange={handlePlaceholderChange}
                value={selectedCoursePlaceholders}
              />
              {isSavedPlaceholder && editPlaceholdersMode && (
                <div css={closeButtonWrapperCss}>
                  <SecondaryButton css={closeButtonCss} onClick={onStopEditPlaceholdersClick}>
                    Close
                  </SecondaryButton>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

NonCourseFields.defaultProps = defaultProps

export default NonCourseFields
